/* eslint-disable react/prop-types */
import React, {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Table,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  TableBody,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Button,
  Drawer,
  Badge,
  TableSortLabel,
  LinearProgress,
  CircularProgress,
} from '@material-ui/core';
import {
  Search,
  FilterList,
  Check,
  Close,
  Edit,
  Delete,
  Visibility,
  PictureAsPdf,
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import axios from 'axios';
import { format, setHours, setMinutes, setSeconds } from 'date-fns';
import {
  assign,
  concat,
  isEmpty,
  find,
  trim,
  isEqual,
  isUndefined,
  forEach,
  isArray,
  isFunction,
  findIndex,
  isObject,
  replace,
} from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Modal } from '~/components';
import { Input, Select, DateTime, Autocomplete } from '~/components/Form';
import * as defaultConstantes from '~/constants/default';
import {
  convertType,
  filterSerialize,
  t,
  number,
  canURL,
  parseDateString,
  replaceSpecialChars,
} from '~/helpers';
import { openDialog, closeDialog } from '~/store/modules/dialog/actions';
import { resetParams } from '~/store/modules/hooks/actions';

import Card from './Card';
import ComponentDelete from './ComponentDelete';
import IconXLS from './IconXLS';
import ModalFilter from './ModalFilter';
import ModalForm from './ModalForm';
import * as S from './styles';
import { TablePaginationActions } from './TablePaginationActions';

export default function UnidataTable({
  dense,
  header,
  custom,
  read,
  redux,
  actions,
  title,
  nowrap,
  create,
  update,
  delete: CRUDDelete,
  toolbar: ToolbarComponent,
  form: FormComponent,
  onRow,
  maxWidth,
  keys,
  find: findComponent,
  remove: removeComponent,
  detach,
  fixed,
  maxHeight,
  pdf,
  excel: xls,
  labelButtonConfirm,
  labelButtonCancell,
  query,
  queryParams,
  view,
  reportTitle,
  reportOrder,
  reportSerialize,
  futureDate,
}) {
  const [isHeader, setIsHeader] = useState([]);
  const [isCustom, setIsCustom] = useState([]);
  const [isActive, setIsActive] = useState({});
  const [drawer, setDrawer] = useState(false);
  const [formData, setFormData] = useState({});
  const [order, setOrder] = useState({ orderfield: '', ordination: 'asc' });
  const [isCreate, setIsCreate] = useState(true);
  const [isModal, setIsModal] = useState(false);
  const [isLeft, setIsLeft] = useState(0);
  const [windowHeight, setWindowHeight] = useState(
    `${window.innerHeight - 239}px`
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 425);

  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [loadingTypeFile, setLoadingTypeFile] = useState('');

  const formRef = useRef(null);
  const modalRef = useRef(null);
  const filterModalRef = useRef(null);
  const containerRef = useRef(null);

  const dispatch = useDispatch();
  const permission = canURL();

  const { title: titleRedux } = useSelector((state) => state.drawer);
  const { token } = useSelector((state) => state.auth);

  const today = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(new Date(), 23), 59), 59),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const isQuery = useMemo(
    () =>
      assign(
        {},
        ...concat(
          isHeader
            .filter(
              (head) =>
                !!head.filter &&
                (!!head.filter.defaultValue || head.filter.type === 'select')
            )
            .map((head) => ({ [head.id]: head.filter.defaultValue || ' ' })),
          isCustom
            .filter((head) => !!head.defaultValue)
            .map((head) => ({ [head.id]: head.defaultValue }))
        )
      ),
    [isHeader, isCustom]
  );

  const isInvisible = useMemo(() => {
    if (!isMobile) {
      const objMobileCustom = isCustom
        .filter(
          (c) => !isUndefined(formData[c.id]) && !isEmpty(trim(formData[c.id]))
        )
        .reduce((a, b) => Object.assign(a, { [b.id]: formData[b.id] }), {});

      return isEmpty(objMobileCustom);
    }

    const objWebCustom = concat(isHeader, isCustom)
      .filter(
        (c) => !isUndefined(formData[c.id]) && !isEmpty(trim(formData[c.id]))
      )
      .reduce((a, b) => Object.assign(a, { [b.id]: formData[b.id] }), {});

    return isEmpty(objWebCustom);
  }, [isMobile, isCustom, isHeader, formData, drawer, isModal]);

  const handleManualSubmitForm = useCallback((key) => {
    if (key === 'Enter') formRef.current.submitForm();
  }, []);

  const handleSort = useCallback((orderfield) => {
    setOrder((state) =>
      state.orderfield === orderfield && state.ordination === 'asc'
        ? { orderfield, ordination: 'desc' }
        : { orderfield, ordination: 'asc' }
    );

    onRow({});
  }, []);

  const handleChangeSelectHeaderTable = useCallback(
    async (value, input) => {
      await formRef.current.setFieldValue(input, trim(value));
      !isMobile && handleManualSubmitForm('Enter');
    },
    [isMobile]
  );

  const handleRenderInputToFilter = useCallback(
    (name, type, label, options, icon, autocomplete) => {
      switch (type) {
        case 'text':
        case 'number':
        default:
          return (
            <Input
              name={name}
              type={type || 'text'}
              label={label}
              variant={icon ? 'standard' : 'outlined'}
              InputProps={
                icon
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }
                  : {}
              }
              onKeyPress={({ key }) => handleManualSubmitForm(key)}
            />
          );
        case 'select':
          return (
            <Select
              name={name}
              options={concat(
                [{ value: ' ', label: 'Todos' }],
                options.map((op) => ({ label: op.value, value: op.label }))
              )}
              variant={icon ? 'standard' : 'outlined'}
              label={label}
              InputProps={
                icon
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }
                  : {}
              }
              onChange={(e) =>
                handleChangeSelectHeaderTable(e.target.value, name)
              }
            />
          );
        case 'datetime':
        case 'initialdatetime':
        case 'finaldatetime':
          return (
            <DateTime
              time
              name={name}
              inputVariant={icon ? 'standard' : 'outlined'}
              label={label}
              onKeyPress={({ key }) => handleManualSubmitForm(key)}
            />
          );
        case 'date':
          return (
            <DateTime
              name={name}
              inputVariant={icon ? 'standard' : 'outlined'}
              label={label}
              onKeyPress={({ key }) => handleManualSubmitForm(key)}
            />
          );
        case 'autocomplete': {
          return (
            <Autocomplete
              name={name}
              label={autocomplete.label}
              url={autocomplete.url}
              unique={autocomplete.unique}
              title={label}
              variant={icon ? 'standard' : 'outlined'}
              onSerialize={autocomplete.serialize}
              getValue={(value) =>
                // isMobile ? undefined : handleManualSubmitForm('Enter')
                isMobile
                  ? undefined
                  : formRef.current.setFieldValue(name, value[name])
              }
            />
          );
        }
      }
    },
    [isMobile]
  );

  const handleGenerateKeys = useCallback(
    (data) => {
      if (isArray(keys)) {
        const url = [];

        forEach(keys, (key) => {
          url.push(data[key]);
        });

        return url.join('/');
      }

      return data[keys];
    },
    [keys]
  );

  const renderCellsHeader = useMemo(
    () =>
      isHeader.map((head, key) => (
        <TableCell
          key={`table-heading-${key}`}
          align={head.align || 'left'}
          padding="default"
          variant="head"
          sortDirection={order.id === head.id ? order.direction : false}
        >
          <TableSortLabel
            active={order.orderfield === head.id}
            direction={order.orderfield === head.id ? order.ordination : 'asc'}
            onClick={() => handleSort(head.id)}
          >
            <p style={{ whiteSpace: 'nowrap' }}>{head.label}</p>
          </TableSortLabel>
          {!isMobile && head.filter && (
            <S.WrapperFilters>
              {handleRenderInputToFilter(
                head.id,
                head.filter.type,
                '',
                head.filter.options,
                true,
                head.filter.autocomplete
              )}
            </S.WrapperFilters>
          )}
        </TableCell>
      )),
    [isHeader, order, isMobile]
  );

  const handleResponseAPI = useCallback(
    async (isQuery) => {
      let dataQuery = assign(isQuery, queryParams);

      if (!isEmpty(query.wherefield)) {
        if (isEmpty(dataQuery)) dataQuery = query;
        else if (isEmpty(dataQuery.wherefield))
          dataQuery = assign(dataQuery, query);
        else {
          dataQuery = {
            ...dataQuery,
            wherefield: concat(dataQuery.wherefield, query.wherefield),
            valuewherefield: concat(
              dataQuery.valuewherefield,
              query.valuewherefield
            ),
            typewherefield: concat(
              dataQuery.typewherefield,
              query.typewherefield
            ),
          };
        }
      }

      await dispatch(read(dataQuery));
      setIsActive({});
      onRow({});

      if (isMobile) window.scrollTo(0, 0);
    },
    [read, dispatch, isMobile, query, queryParams]
  );

  const handleValidDate = useCallback(
    async (values) => {
      try {
        const schema = Yup.object().shape({
          data_cadastro_ini: Yup.date()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .transform((_, value) => parseDateString(value))
            .required(t('s.geral.validacao.campoobrigatorio')),
          data_cadastro_fim: futureDate
            ? Yup.date()
                .typeError(t('s.geral.validacao.campoobrigatorio'))
                .transform((_, value) => parseDateString(value))
                .required(t('s.geral.validacao.campoobrigatorio'))
                .min(
                  Yup.ref('data_cadastro_ini'),
                  'A data não pode ser inferior a Data inicial'
                )
            : Yup.date()
                .typeError(t('s.geral.validacao.campoobrigatorio'))
                .transform((_, value) => parseDateString(value))
                .required(t('s.geral.validacao.campoobrigatorio'))
                .max(today, `A data não pode ser superior a data atual`)
                .min(
                  Yup.ref('data_cadastro_ini'),
                  'A data não pode ser inferior a Data inicial'
                ),
        });

        await schema.validate(values, {
          abortEarly: false,
        });

        formRef.current.setErrors({});
        setDrawer(false);
        return true;
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        } else if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));

        return false;
      }
    },
    [isCustom, today, formRef, futureDate]
  );

  const handleSubmit = useCallback(
    async (data) => {
      const { page, pageSize } = redux;

      let isData = assign(formData, data);
      let customIsValid = true;

      if (data.data_cadastro_ini && data.data_cadastro_fim) {
        customIsValid = await handleValidDate(data);
      }

      const inArray = concat(
        isHeader
          .filter((head) => !!head.filter)
          .map((head) => ({ id: head.id, type: head.filter.type })),
        isCustom
      );

      isCustom
        .filter((custom) => !!custom.defaultValue)
        .forEach((custom) => {
          if (
            findIndex(Object.keys(isData), (key) => key === custom.id) === -1
          ) {
            isData = assign(isData, { [custom.id]: custom.defaultValue });
          }
        });

      const isQuery = filterSerialize(
        Object.keys(isData).map((head) => ({
          name: head,
          type: convertType(find(inArray, (arr) => arr.id === head).type),
          value: trim(isData[head]),
        }))
      );

      if (isEqual(isCustom, custom) && customIsValid) {
        handleResponseAPI({
          page,
          pagesize: pageSize,
          ...(isEmpty(isQuery.wherefield) ? {} : isQuery),
          ...(isEmpty(order.orderfield) ? {} : order),
        });

        setFormData(isData);
        onRow({});
        setDrawer(false);
      }
    },
    [isHeader, isCustom, formData, order, custom, redux]
  );

  const handleChangePage = useCallback(
    (page) => {
      const { pageSize } = redux;
      const inArray = concat(
        isHeader
          .filter((head) => !!head.filter)
          .map((head) => ({ id: head.id, type: head.filter.type })),
        isCustom
      );

      const query = filterSerialize(
        Object.keys(formData).map((head) => ({
          name: head,
          type: convertType(find(inArray, (arr) => arr.id === head).type),
          value: trim(formData[head]),
        }))
      );

      handleResponseAPI({
        pagesize: pageSize,
        page,
        ...(isEmpty(query.wherefield) ? {} : query),
        ...(isEmpty(order.orderfield) ? {} : order),
      });
      onRow({});
    },
    [redux, formData, isHeader, isCustom, order]
  );

  const handleChangeRowsPerPage = useCallback(
    (pagesize) => {
      const inArray = concat(
        isHeader
          .filter((head) => !!head.filter)
          .map((head) => ({ id: head.id, type: head.filter.type })),
        isCustom
      );

      const query = filterSerialize(
        Object.keys(formData).map((head) => ({
          name: head,
          type: convertType(find(inArray, (arr) => arr.id === head).type),
          value: trim(formData[head]),
        }))
      );

      handleResponseAPI({
        page: 1,
        pagesize,
        ...(isEmpty(query.wherefield) ? {} : query),
        ...(isEmpty(order.orderfield) ? {} : order),
      });
      onRow({});
    },
    [formData, isHeader, isCustom, order]
  );

  const handleSubmitFormToDrawer = useCallback(() => {
    formRef.current.submitForm();
  }, []);

  const handleSubmitFormToModalFilter = useCallback(() => {
    formRef.current.submitForm();
    filterModalRef.current.handleCloseModal();

    setIsModal(false);
  }, []);

  const handleOpenModal = useCallback(() => {
    dispatch(openDialog());
    setIsCreate(true);
  }, []);

  const handleClickRow = useCallback((data) => {
    onRow(data);
    setIsActive(data);
  }, []);

  const handlegetUniqueItem = useCallback(
    async (data) => {
      await dispatch(findComponent(handleGenerateKeys(data)));

      setIsCreate(false);
      setEditing(true);
    },
    [redux]
  );

  const handleDeleteUniqueItem = useCallback(() => {
    dispatch(removeComponent(handleGenerateKeys(isActive), redux.params));
    setIsActive({});
    setRemoving(true);
  }, [isActive, redux]);

  const handleOpenModalFilter = useCallback(() => {
    filterModalRef.current.handleOpenModal();
    setIsModal(true);
  }, []);

  const handleExportCallback = useCallback(
    async (type) => {
      try {
        setLoadingFile(true);
        setLoadingTypeFile(type);

        const { REACT_APP_NOME_SISTEMA } = !isUndefined(
          process.env.REACT_APP_NOME_SISTEMA
        )
          ? process.env
          : defaultConstantes;

        const inArray = concat(
          isHeader
            .filter((head) => !!head.filter)
            .map((head) => ({ id: head.id, type: head.filter.type })),
          isCustom
        );

        const query = filterSerialize(
          Object.keys(formData).map((head) => ({
            name: head,
            type: convertType(find(inArray, (arr) => arr.id === head).type),
            value: trim(formData[head]),
          }))
        );

        // Formatting SQL Injection
        const formatterSQLInjection = (typeInjection, value) => {
          switch (typeInjection) {
            case 'MENOR_IGUAL':
              return `<=  '${value}'`;
            case 'MAIOR_IGUAL':
              return `>=  '${value}'`;
            case 'EQUALS':
              return `= '${value}'`;
            case 'LIKE':
            default:
              return `${typeInjection} '%${value}%'`;
          }
        };

        // Ordenation
        const orderBy = order.orderfield
          ? `${order.orderfield} ${order.ordination.toUpperCase()}`
          : reportOrder;

        // SQL Injection
        const sqlInjection = [];

        forEach(query.wherefield, (sql, index) => {
          sqlInjection.push(
            `${sql} ${formatterSQLInjection(
              query.typewherefield[index],
              query.valuewherefield[index]
            )}`
          );
        });

        const SQLInjection = isEmpty(sqlInjection)
          ? ''
          : `where ${sqlInjection.join(' AND ')}`.toString();

        // STRING SQL Injection
        const arraySQLInjection = [];

        forEach(query.wherefield, (sql, index) => {
          arraySQLInjection.push(`${sql}: ${query.valuewherefield[index]}`);
        });

        const strInjection = arraySQLInjection.join(' | ');
        const headerSerialize = reportSerialize(
          header.filter((h) => !isEmpty(h) && isObject(h))
        );

        // HEADERS
        const headers = {};

        forEach(headerSerialize, (h, index) => {
          headers[`headerText${index}`] = h.label;
          headers[`alignText${index}`] = h.align || 'left';
          headers[`precisionValue${index}`] = h.precision || '';
          headers[`pesquisa${index}`] = '';
          headers[`dataField${index}`] = h.id;
          headers[`pattern${index}`] = h.pattern || '';
        });

        const exportObject = {
          formato: type,
          fieldCount: headerSerialize.length.toString(),
          download: type === 'xls' ? '1' : '',
          view,
          where: SQLInjection,
          max_reg: redux.totalCount.toString(),
          orderBy,
          textoPesquisa: strInjection,
          tituloRelatorio: reportTitle,
          nomeSistema: JSON.parse(`"${REACT_APP_NOME_SISTEMA}"`),
          ...headers,
        };

        const { REACT_APP_URL_EXPORT } = process.env;

        const url = new URL(
          `${REACT_APP_URL_EXPORT}/relatorios?relatorio=DownloadArquivo`
        );

        const response = await axios({
          url,
          method: 'POST',
          responseType: 'blob',
          headers: { token },
          data: exportObject,
        });


        /*if ( response.data.type.includes('pdf1') ) {        
          //var file = new Blob([response.data], {type: 'application/pdf'});
          let file = new File([response.data], reportTitle  + '.pdf', {type: "application/pdf"});

          var fileURL = URL.createObjectURL(file);          
          window.open(fileURL);

          //let fileURL = URL.createObjectURL(file);
          //window.location.assign(fileURL);

        } else {
          */
        let fileName = reportTitle;
        //const file = new Blob([response.data], { type: response.data.type });
        const file = new Blob([response.data], { type: 'octet/stream' });
        const a = document.createElement('a');

        document.body.appendChild(a);

        const objectUrl = window.URL.createObjectURL(file);
        a.href = objectUrl;
        a.download = response.data.type.includes('pdf') ? fileName + '.pdf' : fileName + '.xls';

        a.rel = 'noopener';
        a.target = '_blank';

        a.click();
        document.body.removeChild(a);
        //}

        /*
                let fileName = reportTitle;
                const file = new Blob([response.data], { type: response.data.type });
                const a = document.createElement('a');
                const objectUrl = window.URL.createObjectURL(file);
                a.href = objectUrl;
                a.download = response.data.type.includes('pdf') ? fileName : fileName + '.xls'; 
                a.click();
                document.body.removeChild(a);
                */


      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      } finally {
        setLoadingFile(false);
      }
    },
    [
      formData,
      isHeader,
      isCustom,
      redux,
      order,
      view,
      reportTitle,
      reportOrder,
      header,
      token,
      reportSerialize,
    ]
  );

  const handleCloseForm = useCallback(() => {
    dispatch(dispatch(closeDialog()));
    setIsCreate(false);
  }, []);

  const ActionsModal = useMemo(
    () => [
      {
        label: t('s.geral.botaoconfirmar'),
        callback: () => handleDeleteUniqueItem(),
        color: 'primary',
        autoFocus: true,
      },
      {
        label: t('s.geral.botaofechar'),
        callback: () => modalRef.current.handleCloseModal(),
        color: 'secondary',
      },
    ],
    [isActive]
  );

  const ActionsModalFilter = useMemo(
    () => [
      {
        label: t('s.geral.botaoconfirmar'),
        callback: () => handleSubmitFormToModalFilter(),
        color: 'primary',
        autoFocus: true,
      },
      {
        label: t('s.geral.botaocancelar'),
        callback: () => {
          filterModalRef.current.handleCloseModal();
          setIsModal(false);
        },
        color: 'secondary',
      },
    ],
    []
  );

  useEffect(() => {
    if (!isEqual(header, isHeader)) {
      setIsHeader(header);
    }
  }, [header, isHeader]);

  useEffect(() => {
    if (!isEqual(custom, isCustom)) {
      setIsCustom(custom);
    }
  }, [custom, isCustom]);

  useEffect(() => {
    !isEmpty(isQuery) && handleManualSubmitForm('Enter');
  }, [isQuery]);

  useEffect(() => {
    if (!isEmpty(order.orderfield)) {
      const { pageSize } = redux;

      const inArray = concat(
        isHeader
          .filter((head) => !!head.filter)
          .map((head) => ({ id: head.id, type: head.filter.type })),
        isCustom
      );
      const headerData = assign(formData, formRef.current.getData());

      const query = filterSerialize(
        Object.keys(headerData).map((head) => ({
          name: head,
          type: convertType(find(inArray, (arr) => arr.id === head).type),
          value: trim(headerData[head]),
        }))
      );

      handleResponseAPI({
        page: 1,
        pagesize: pageSize,
        ...(isEmpty(query.wherefield) ? {} : query),
        ...(isEmpty(order.orderfield) ? {} : order),
      });
    }
  }, [order, isHeader, isCustom, formRef, formData]);

  useEffect(() => {
    if (editing && !redux.loading && !isEmpty(isActive)) {
      dispatch(openDialog());
      setEditing(false);
    }
  }, [editing, redux, isActive]);

  useEffect(() => {
    if (removing && !redux.loading) {
      setRemoving(false);
      modalRef.current.handleCloseModal();
    }
  }, [removing, redux]);

  useEffect(() => {
    if (maxHeight) setWindowHeight(maxHeight);
  }, [maxHeight]);

  useEffect(() => {
    const notRead = header
      .filter((head) => !!head.filter)
      .filter((head) => head.filter.type === 'select');

    isEmpty(notRead) && handleManualSubmitForm('Enter');
  }, [header]);

  window.addEventListener('resize', () => {
    // check is mobile
    if (window.innerWidth <= 500) setIsMobile(true);
    else setIsMobile(false);

    // change height
    if (!maxHeight) {
      setWindowHeight(`${window.innerHeight - 239}px`);
    }
  });

  return (
    <S.Wrapper
      ref={formRef}
      onSubmit={handleSubmit}
      initialData={assign(isQuery, formData)}
    >
      {redux.loading && <LinearProgress />}
      <Paper elevation={isMobile ? 0 : 2}>
        <S.Toolbar>
          {!!title && <Typography variant="h6">{title}</Typography>}
          <S.ToolbarActions>
            {!!ToolbarComponent && <ToolbarComponent />}

            {permission.create && create && (
              <Button
                color="primary"
                size="small"
                variant="contained"
                onClick={() => handleOpenModal({})}
              >
                {t('s.geral.botaoincluir')}
              </Button>
            )}

            {pdf && (
              <IconButton
                disabled={loadingFile}
                color={loadingFile ? 'default' : 'primary'}
                onClick={() => handleExportCallback('pdf')}
              >
                {loadingFile && loadingTypeFile === 'pdf' ? (
                  <CircularProgress size="2rem" color="inherit" />
                ) : (
                  <PictureAsPdf />
                )}
              </IconButton>
            )}

            {xls && (
              <IconButton
                disabled={loadingFile}
                color={loadingFile ? 'default' : 'primary'}
                onClick={() => handleExportCallback('excel')}
              >
                {loadingFile && loadingTypeFile === 'excel' ? (
                  <CircularProgress size="2rem" color="inherit" />
                ) : (
                  <IconXLS />
                )}
              </IconButton>
            )}

            {(isMobile || !isEmpty(custom)) && (
              <IconButton
                color="primary"
                size="small"
                onClick={() =>
                  isMobile ? handleOpenModalFilter() : setDrawer(true)
                }
              >
                <Badge color="secondary" variant="dot" invisible={isInvisible}>
                  <FilterList />
                </Badge>
              </IconButton>
            )}
          </S.ToolbarActions>
        </S.Toolbar>

        {isMobile ? (
          <>
            {redux.data.map((data, index) => (
              <Card
                key={`card-${index}`}
                detach={detach}
                labels={isHeader}
                data={data}
                permission={permission}
                actions={actions}
                onEdit={() => handlegetUniqueItem(data)}
                onDelete={() => modalRef.current.handleOpenModal()}
                onSelect={() => handleClickRow(data)}
                update={update}
                delete={CRUDDelete}
              />
            ))}

            <S.WrapperPagination>
              <Pagination
                count={redux.totalPages}
                variant="outlined"
                shape="rounded"
                onChange={(e, page) => handleChangePage(page)}
              />
            </S.WrapperPagination>
          </>
        ) : (
          <Paper variant="outlined" style={{ border: 0 }}>
            <S.WrapperContainer
              ref={containerRef}
              maxHeight={windowHeight}
              onScroll={() => setIsLeft(containerRef.current.scrollLeft)}
            >
              <Table
                aria-labelledby="unidataTable"
                size={dense ? 'small' : 'medium'}
                stickyHeader={fixed}
              >
                <TableHead>
                  <TableRow>{renderCellsHeader}</TableRow>
                </TableHead>
                <TableBody>
                  {redux.data.map((data, index) => (
                    <TableRow
                      key={`cell-body-${index}`}
                      onClick={() => handleClickRow(data)}
                      selected={isEqual(data, isActive)}
                    >
                      {isHeader.map(
                        (
                          { id, formatted, styled, onDelete, onUpdate, align },
                          key
                        ) => (
                          <S.Cell
                            key={`cell-body-${index}-${key}`}
                            nowrap={Number(nowrap)}
                            style={isFunction(styled) ? styled(data) : {}}
                            align={align || 'left'}
                          >
                            {key === 0 &&
                              (!isEmpty(actions) || update || CRUDDelete) && (
                                <S.GroupButtons
                                  show={Number(isEqual(data, isActive))}
                                  isLeft={isLeft}
                                >
                                  {actions.map((ac) => (
                                    <Tooltip title={ac.title}>
                                      <IconButton
                                        key={ac.title}
                                        color={ac.color || 'default'}
                                        disabled={ac.disabled || false}
                                        size="small"
                                        onClick={() => ac.action(data)}
                                      >
                                        {ac.icon}
                                      </IconButton>
                                    </Tooltip>
                                  ))}

                                  {(permission.update ||
                                    isEqual(permission, { read: true })) &&
                                    (isUndefined(onUpdate) || onUpdate(data)) &&
                                    update && (
                                      <Tooltip
                                        title={t(
                                          isEqual(permission, { read: true })
                                            ? 's.geral.botaomodelo'
                                            : 's.geral.botaoalterar'
                                        )}
                                      >
                                        <IconButton
                                          color="default"
                                          size="small"
                                          onClick={() =>
                                            handlegetUniqueItem(data)
                                          }
                                        >
                                          {isEqual(permission, {
                                            read: true,
                                          }) ? (
                                            <Visibility />
                                          ) : (
                                            <Edit />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    )}

                                  {permission.delete &&
                                    isFunction(removeComponent) &&
                                    (isUndefined(onDelete) || onDelete(data)) &&
                                    CRUDDelete && (
                                      <Tooltip
                                        title={t('s.geral.botaoexcluir')}
                                      >
                                        <IconButton
                                          color="default"
                                          size="small"
                                          onClick={() =>
                                            modalRef.current.handleOpenModal()
                                          }
                                        >
                                          <Delete />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                </S.GroupButtons>
                              )}

                            {formatted ? formatted(data[id]) : data[id]}
                          </S.Cell>
                        )
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </S.WrapperContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
              component="div"
              count={redux.totalCount}
              rowsPerPage={redux.pageSize}
              page={redux.page - 1}
              labelRowsPerPage={t('s.geral.linhasporpagina')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${number(to, 0)} ${t('s.geral.de')} ${number(
                  count,
                  0
                )}`
              }
              onChangePage={(e, page) => handleChangePage(page + 1)}
              onChangeRowsPerPage={(e) =>
                handleChangeRowsPerPage(e.target.value)
              }
              ActionsComponent={(props) => (
                <TablePaginationActions
                  {...props}
                  page={redux.page}
                  onPageChange={handleChangePage}
                />
              )}
            />
          </Paper>
        )}
      </Paper>

      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)}>
        <S.WrapperDrawer>
          {custom.map((c) => {
            return handleRenderInputToFilter(
              c.id,
              c.type,
              c.label,
              c.options,
              false,
              c.autocomplete
            );
          })}
        </S.WrapperDrawer>
        <S.GroupButtonsDrawer>
          <Button
            fullWidth
            startIcon={<Check />}
            color="primary"
            variant="outlined"
            type="button"
            onClick={() => handleSubmitFormToDrawer()}
          >
            {t('s.geral.botaoconfirmar')}
          </Button>

          <Button
            fullWidth
            startIcon={<Close />}
            color="secondary"
            variant="outlined"
            type="button"
            onClick={() => {
              setDrawer(false);
              formRef.current.setErrors({});
            }}
          >
            {t('s.geral.botaocancelar')}
          </Button>
        </S.GroupButtonsDrawer>
      </Drawer>

      <ModalForm
        component={FormComponent}
        maxWidth={maxWidth}
        fullScreen={isMobile}
        loading={redux.loading}
        create={isCreate}
        btnConfirm={labelButtonConfirm}
        btnCancell={labelButtonCancell}
        onClose={handleCloseForm}
      />

      <Modal
        disableBackdropClick
        disableEscapeKeyDown
        ref={modalRef}
        maxWidth="xs"
        title={t('s.geral.botaoexcluir')}
        component={ComponentDelete}
        loading={redux.loading}
        actions={ActionsModal}
      />

      <Modal
        fullScreen
        ref={filterModalRef}
        title={titleRedux}
        component={() => (
          <S.WrapperFilters>
            <ModalFilter
              inputs={concat(
                isHeader
                  .filter((head) => !!head.filter)
                  .map((head) => ({
                    id: head.id,
                    label: head.label,
                    type: head.filter.type,
                    options: head.filter.options,
                    autocomplete: head.filter.autocomplete,
                  })),
                isCustom
              )}
              formatter={handleRenderInputToFilter}
            />
          </S.WrapperFilters>
        )}
        loading={redux.loading}
        actions={ActionsModalFilter}
      />
    </S.Wrapper>
  );
}

UnidataTable.propTypes = {
  dense: PropTypes.bool,
  nowrap: PropTypes.bool,
  header: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      formatted: PropTypes.func,
      styled: PropTypes.func,
      onDelete: PropTypes.func,
      onUpdate: PropTypes.func,
      align: PropTypes.string,
      filter: PropTypes.shape({
        type: PropTypes.string.isRequired,
        defaultValue: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
          })
        ),
        autocomplete: PropTypes.shape({
          label: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          unique: PropTypes.string.isRequired,
        }),
        precision: PropTypes.string,
        pattern: PropTypes.string,
      }),
    })
  ).isRequired,
  custom: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      defaultValue: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  read: PropTypes.func.isRequired,
  redux: PropTypes.objectOf(
    PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.arrayOf(PropTypes.any).isRequired,
      find: PropTypes.objectOf(PropTypes.any).isRequired,
      page: PropTypes.number.isRequired,
      pageSize: PropTypes.number.isRequired,
      totalPages: PropTypes.number.isRequired,
      totalCount: PropTypes.number.isRequired,
    })
  ).isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
      action: PropTypes.func.isRequired,
      color: PropTypes.oneOf(['primary', 'secondary', 'default']),
      disabled: PropTypes.bool,
    })
  ),
  title: PropTypes.string,
  create: PropTypes.bool,
  update: PropTypes.bool,
  delete: PropTypes.bool,
  toolbar: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  onRow: PropTypes.func,
  form: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  maxWidth: PropTypes.string,
  keys: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  find: PropTypes.func,
  remove: PropTypes.func,
  detach: PropTypes.string.isRequired,
  fixed: PropTypes.bool,
  maxHeight: PropTypes.string,
  pdf: PropTypes.bool,
  excel: PropTypes.bool,
  onExport: PropTypes.func,
  labelButtonConfirm: PropTypes.string,
  labelButtonCancell: PropTypes.string,
  query: PropTypes.objectOf(
    PropTypes.shape({
      wherefield: PropTypes.arrayOf(PropTypes.string).isRequired,
      valuewherefield: PropTypes.arrayOf(PropTypes.string).isRequired,
      typewherefield: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ),
  queryParams: PropTypes.objectOf(PropTypes.any),
  view: PropTypes.string,
  reportTitle: PropTypes.string,
  reportOrder: PropTypes.string,
  reportSerialize: PropTypes.func,
};

UnidataTable.defaultProps = {
  dense: false,
  nowrap: false,
  custom: [],
  actions: [],
  title: '',
  create: true,
  update: true,
  delete: true,
  toolbar: undefined,
  onRow: () => undefined,
  form: undefined,
  maxWidth: 'sm',
  find: () => undefined,
  remove: () => undefined,
  fixed: false,
  maxHeight: '',
  pdf: false,
  excel: false,
  onExport: () => undefined,
  labelButtonConfirm: 's.geral.botaoconfirmar',
  labelButtonCancell: 's.geral.botaocancelar',
  query: {
    wherefield: [],
    valuewherefield: [],
    typewherefield: [],
  },
  queryParams: {},
  view: '',
  reportTitle: '',
  reportOrder: '',
  reportSerialize: (value) => value,
};
